import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Stack,
    // IconButton, // Unused
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Toast from '../components/Toast';
import SectionWrapper from '../components/SectionWrapper';
import { trackEvent, trackLinkedInLeadFormSubmitConversion, trackMetaLeadFormSubmitConversion } from '../shared/analytics/ga4';

const deepVoid = '#FFFFFF'; // Pure White
const structuralGrey = '#E0E0E0'; // Light Grey for Grid
const textBlack = '#050505'; // Ink Black
const textGrey = '#666666'; // Neutral Grey
const CONTACT_DRAFT_STORAGE_KEY = 'r5_contact_form_draft_desktop_v2';
const CONTACT_REQUIRED_FIELDS = ['name', 'contact', 'message'];
const CONTACT_OPERATIONAL_PATTERN = /\b(test|teste|smtp|codex|qa|homolog|interno)\b/i;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

const looksLikeEmailInput = (value) => /[A-Za-z@]/.test(String(value || ''));

const formatPhoneDisplay = (value) => {
    let raw = String(value || '').replace(/\D/g, '');
    if (raw.length > 11) raw = raw.substring(0, 11);
    let formatted = '';
    if (raw.length > 0) {
        formatted = `(${raw.substring(0, 2)}`;
        if (raw.length > 2) formatted += `) ${raw.substring(2, 7)}`;
        if (raw.length > 7) formatted += `-${raw.substring(7, 11)}`;
    }
    return formatted;
};

const resolveContactDetails = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return { kind: 'empty', email: '', phoneDisplay: '', phonePayload: '' };

    if (looksLikeEmailInput(trimmed)) {
        return {
            kind: 'email',
            email: trimmed.toLowerCase(),
            phoneDisplay: '',
            phonePayload: ''
        };
    }

    const phoneDisplay = formatPhoneDisplay(trimmed);
    const digits = phoneDisplay.replace(/\D/g, '');
    return {
        kind: 'phone',
        email: '',
        phoneDisplay,
        phonePayload: digits ? `+55 ${phoneDisplay}` : ''
    };
};

const readDraft = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(CONTACT_DRAFT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            name: String(parsed.name || ''),
            contact: String(parsed.contact || parsed.email || parsed.phone || ''),
            message: String(parsed.message || ''),
            whatsappConsent: Boolean(parsed.whatsappConsent)
        };
    } catch (error) {
        return null;
    }
};

const persistDraft = (draft) => {
    if (typeof window === 'undefined') return;
    const normalized = {
        name: String(draft?.name || ''),
        contact: String(draft?.contact || ''),
        message: String(draft?.message || ''),
        whatsappConsent: Boolean(draft?.whatsappConsent)
    };

    try {
        if (!Object.values(normalized).some((value) => String(value || '').trim())) {
            window.sessionStorage.removeItem(CONTACT_DRAFT_STORAGE_KEY);
            return;
        }
        window.sessionStorage.setItem(CONTACT_DRAFT_STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
        // Ignore storage errors in privacy-restricted browsers.
    }
};

const clearDraft = () => {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(CONTACT_DRAFT_STORAGE_KEY);
    } catch (error) {
        // Ignore storage errors in privacy-restricted browsers.
    }
};

const isOperationalLead = (payload = {}) => {
    const contact = resolveContactDetails(payload.contact || payload.email || payload.phone || '');
    const email = String(contact.email || payload.email || '').trim().toLowerCase();
    if (email.endsWith('@r5id.com.br')) return true;

    const haystack = [payload.name, payload.contact, payload.email, payload.phone, payload.message]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' ');

    return CONTACT_OPERATIONAL_PATTERN.test(haystack);
};

const ContactPage = () => {
    const theme = useTheme();
    // eslint-disable-next-line no-unused-vars
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // FORM STATE
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        message: '',
        whatsappConsent: false
    });
    const [status, setStatus] = useState('idle');
    const [toast, setToast] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [errors, setErrors] = useState({});
    const leadStartTrackedRef = useRef(false);
    const leadSubmitCompletedRef = useRef(false);
    const leadAbandonTrackedRef = useRef(false);
    const formPageContextRef = useRef(null);
    const formDataRef = useRef(formData);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    useEffect(() => {
        formPageContextRef.current = {
            pathname: window.location.pathname,
            search: window.location.search,
            pageUrl: window.location.href,
            pageTitle: document.title
        };
    }, []);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const getFormTrackingOptions = useCallback((metadata = {}, snapshot = formDataRef.current) => ({
        ...(formPageContextRef.current || {}),
        metadata: {
            ...metadata,
            operational: isOperationalLead(snapshot)
        }
    }), []);

    useEffect(() => {
        const draft = readDraft();
        if (!draft || !Object.values(draft).some((value) => String(value || '').trim())) return;
        setFormData(draft);
        trackEvent('lead_form_draft_restored', {
            ...getFormTrackingOptions({
                funnel: 'lead',
                step: 'draft_restored',
                formId: 'contact',
                restoredFields: CONTACT_REQUIRED_FIELDS.filter((field) => String(draft[field] || '').trim())
            }, draft),
            allowOperational: true
        });
    }, [getFormTrackingOptions]);

    const focusField = (fieldName) => {
        if (!fieldName || typeof document === 'undefined') return;
        const field = document.querySelector(`[name="${fieldName}"]`);
        if (!field) return;
        field.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        field.focus?.();
    };

    const trackLeadStart = (fieldName, fieldValue) => {
        if (leadStartTrackedRef.current) return;
        if (!String(fieldValue || '').trim()) return;
        leadStartTrackedRef.current = true;
        leadSubmitCompletedRef.current = false;
        leadAbandonTrackedRef.current = false;
        trackEvent('lead_form_start', getFormTrackingOptions({
            funnel: 'lead',
            step: 'start',
            formId: 'contact',
            fieldName,
        }));
    };

    useEffect(() => {
        const trackLeadAbandon = (trigger) => {
            if (!leadStartTrackedRef.current || leadSubmitCompletedRef.current || leadAbandonTrackedRef.current) return;
            leadAbandonTrackedRef.current = true;
            trackEvent('lead_form_abandon', getFormTrackingOptions({
                funnel: 'lead',
                step: 'abandon',
                formId: 'contact',
                trigger,
            }));
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                trackLeadAbandon('visibility_hidden');
            }
        };

        const handlePageHide = () => trackLeadAbandon('pagehide');
        const handleBeforeUnload = () => trackLeadAbandon('beforeunload');

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            trackLeadAbandon('unmount');
        };
    }, [getFormTrackingOptions]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;
        trackLeadStart(name, nextValue);

        if (name === 'contact') {
            const formatted = looksLikeEmailInput(value) ? value : formatPhoneDisplay(value);
            setFormData((prev) => {
                const next = { ...prev, contact: formatted };
                persistDraft(next);
                return next;
            });
        } else if (name === 'whatsappConsent') {
            setFormData((prev) => {
                const next = { ...prev, whatsappConsent: checked };
                persistDraft(next);
                return next;
            });
        } else {
            setFormData((prev) => {
                const next = { ...prev, [name]: value };
                persistDraft(next);
                return next;
            });
        }

        setErrors((prev) => {
            if (!prev || !prev[name]) return prev;
            const next = { ...prev };
            const trimmed = String(value || '').trim();
            if (name === 'contact') {
                const details = resolveContactDetails(value);
                const digits = details.phoneDisplay.replace(/\D/g, '');
                const isValidEmail = details.kind === 'email' && EMAIL_PATTERN.test(trimmed);
                const isValidPhone = details.kind === 'phone' && digits.length >= 10;
                if (isValidEmail || isValidPhone) delete next.contact;
            } else if (name === 'whatsappConsent') {
                if (!checked) delete next.contact;
            } else if (trimmed) {
                delete next[name];
            }
            return next;
        });
    };

    const getValidationErrors = () => {
        const next = {};
        const contact = resolveContactDetails(formData.contact);
        const phoneDigits = contact.phoneDisplay.replace(/\D/g, '');

        if (!String(formData.name || '').trim()) next.name = 'required';
        if (contact.kind === 'empty') next.contact = 'required';
        else if (contact.kind === 'email' && !EMAIL_PATTERN.test(contact.email)) next.contact = 'invalid_email';
        else if (contact.kind === 'phone' && phoneDigits.length < 10) next.contact = 'invalid_phone';
        if (!String(formData.message || '').trim()) next.message = 'required';

        return next;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Some mobile browsers can accidentally trigger form submission when tapping
        // nearby links. Only validate/send when the submit button was used or when
        // the user submits from a focused field (Enter).
        const submitter = e?.nativeEvent?.submitter;
        const submitterUI = submitter?.getAttribute?.('data-ui');
        const active = document.activeElement;
        const activeTag = active?.tagName;
        const isFromField =
            !!active &&
            (activeTag === 'INPUT' || activeTag === 'TEXTAREA') &&
            e.currentTarget?.contains?.(active);
        if (submitter && submitterUI !== 'contact-submit') return;
        if (!submitter && !isFromField) return;

        trackEvent('lead_form_submit_intent', getFormTrackingOptions({
            funnel: 'lead',
            step: 'submit_intent',
            formId: 'contact',
        }));

        const validationErrors = getValidationErrors();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            trackEvent('lead_form_validation_error', getFormTrackingOptions({
                funnel: 'lead',
                step: 'validation_error',
                formId: 'contact',
                invalidFields: Object.keys(validationErrors),
                errorCount: Object.keys(validationErrors).length,
                name: formData.name,
                email: resolveContactDetails(formData.contact).email,
            }));
            showToast('Por favor, preencha os campos obrigatórios.', 'error');
            focusField(CONTACT_REQUIRED_FIELDS.find((field) => validationErrors[field]) || (validationErrors.contact ? 'contact' : null));
            return;
        }
        setErrors({});
        setStatus('submitting');
        try {
            const contact = resolveContactDetails(formData.contact);
            trackEvent('lead_form_submit_attempt', getFormTrackingOptions({
                funnel: 'lead',
                step: 'submit_attempt',
                formId: 'contact',
                name: formData.name,
                email: contact.email,
            }));
            const { sendContactMessage } = await import('../services/api');
            const payload = {
                name: formData.name,
                email: contact.email,
                phone: contact.phonePayload,
                message: formData.message,
                confirmationConsent: Boolean(formData.whatsappConsent && contact.kind !== 'empty'),
                confirmationChannel: contact.kind === 'email' ? 'email' : contact.kind === 'phone' ? 'whatsapp' : '',
                whatsappConsent: Boolean(formData.whatsappConsent && contact.kind === 'phone' && contact.phonePayload)
            };
            await sendContactMessage(payload);
            trackEvent('lead_form_submit_success', getFormTrackingOptions({
                funnel: 'lead',
                step: 'submit_success',
                formId: 'contact',
            }));
            trackLinkedInLeadFormSubmitConversion();
            trackMetaLeadFormSubmitConversion();
            setStatus('success');
            setErrors({});
            setFormData({ name: '', contact: '', message: '', whatsappConsent: false });
            clearDraft();
            leadStartTrackedRef.current = false;
            leadSubmitCompletedRef.current = true;
            leadAbandonTrackedRef.current = false;
            showToast('Mensagem enviada com sucesso.', 'success');
        } catch (error) {
            console.error('Failed to send message:', error);
            trackEvent('lead_form_submit_error', getFormTrackingOptions({
                funnel: 'lead',
                step: 'submit_error',
                formId: 'contact',
                reason: (error && error.message ? String(error.message) : 'unknown').slice(0, 180),
            }));
            setStatus('error');
            showToast('Falha ao enviar mensagem.', 'error');
        } finally {
            if (status !== 'idle') setStatus('idle');
        }
    };

    const inputStyles = {
        width: '100%',
        padding: '16px',
        background: '#F5F5F5', // Light Grey input bg
        border: '1px solid #E0E0E0',
        color: textBlack,
        fontFamily: '"Inter", sans-serif',
        fontSize: '1rem',
        outline: 'none',
        borderRadius: '14px', // Sharper
        transition: 'all 0.2s ease',
        boxSizing: 'border-box'
    };

    const focusStyles = (e) => {
        e.target.style.borderColor = textBlack;
        e.target.style.background = '#FFFFFF';
    };

    const blurStyles = (e) => {
        e.target.style.borderColor = '#E0E0E0';
        e.target.style.background = '#F5F5F5';
    };


    return (
        <SectionWrapper
            id="contact-page"
            sx={{
                bgcolor: deepVoid,
                color: textBlack,
                fontFamily: '"JetBrains Mono", monospace'
            }}
        >
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* MAIN LAYOUT CONTAINER */}
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden',
                position: 'relative',
                bgcolor: '#FAFAFA'
            }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0.5,
                        pointerEvents: 'none',
                        backgroundImage: `linear-gradient(${structuralGrey} 1px, transparent 1px), linear-gradient(90deg, ${structuralGrey} 1px, transparent 1px)`,
                        backgroundSize: '40px 40px',
                        zIndex: 0
                    }}
                />

                {/* LEFT SECTOR: INFO & CONTEXT */}
                <Box sx={{
                    width: { xs: '100%', md: '45%' },
                    height: { xs: 'auto', md: '100%' },
                    minHeight: { xs: 'auto', md: 0 },
                    borderRight: `1px solid ${structuralGrey}`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: { xs: 3, md: 5 },
                    position: 'relative',
                    zIndex: 1,
                    bgcolor: 'transparent',
                    overflowY: 'auto'
                }}>
                    <Stack spacing={4} sx={{ position: 'relative', zIndex: 2 }}>
                        <Box>
                            <Typography variant="overline" sx={{
                                fontFamily: 'JetBrains Mono',
                                color: textGrey,
                                letterSpacing: '0.2em',
                                fontWeight: 600
                            }}>
                                R5 CONEXÃO
                            </Typography>
                            <Typography variant="h1" sx={{
                                fontSize: { xs: '2.5rem', md: '4rem' },
                                fontWeight: 800,
                                lineHeight: 0.9,
                                letterSpacing: '-0.05em',
                                color: textBlack,
                                mt: 1
                            }}>
                                ENTRE EM<br />
                                CONTATO<span style={{ color: '#CC0000' }}>_</span>
                            </Typography>
                        </Box>

                        {/* RAW LINKS - Clean Black/Grey */}
                        <Stack spacing={2}>
		                            <Box sx={{ display: 'flex', gap: 2 }}>
	                                <a
	                                    href="https://www.linkedin.com/company/103669965/"
	                                    target="_blank"
	                                    rel="noreferrer"
	                                    style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}
	                                    aria-label="LinkedIn"
	                                >
	                                    <LinkedInIcon sx={{ color: textBlack, fontSize: 28, '&:hover': { opacity: 0.7, cursor: 'pointer' } }} />
	                                </a>
	                                <a
	                                    href="https://www.instagram.com/r5inteligenciadigital/"
	                                    target="_blank"
	                                    rel="noreferrer"
	                                    style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}
	                                    aria-label="Instagram"
	                                >
	                                    <InstagramIcon sx={{ color: textBlack, fontSize: 28, '&:hover': { opacity: 0.7, cursor: 'pointer' } }} />
	                                </a>
	                                <a
	                                    href="mailto:contato@r5id.com.br"
	                                    target="_blank"
	                                    rel="noreferrer"
	                                    style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}
	                                    aria-label="Email"
	                                >
	                                    <MailOutlineIcon sx={{ color: textBlack, fontSize: 28, '&:hover': { opacity: 0.7, cursor: 'pointer' } }} />
	                                </a>
	                                <a href="https://wa.me/5561982356730" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }}>
	                                    <WhatsAppIcon sx={{ color: textBlack, fontSize: 28, '&:hover': { opacity: 0.7, cursor: 'pointer' } }} />
	                                </a>
	                            </Box>
	                        </Stack>

                        <Box
                            sx={{
                                borderTop: `1px solid ${structuralGrey}`,
                                borderBottom: `1px solid ${structuralGrey}`,
                                bgcolor: 'transparent',
                                px: 0,
                                py: 2.5
                            }}
                        >
                            <Stack spacing={1.5} alignItems="flex-start">
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        letterSpacing: '-0.03em',
                                        color: textBlack
                                    }}
                                >
                                    Estamos contratando
                                </Typography>
                                <Typography variant="body2" sx={{ color: textGrey, lineHeight: 1.6 }}>
                                    Conheça as vagas disponíveis.
                                </Typography>
                                <Button
                                    component={RouterLink}
                                    to="/trabalhe-conosco"
                                    variant="outlined"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        borderRadius: '14px',
                                        px: 3,
                                        py: 1.2,
                                        color: textBlack,
                                        borderColor: structuralGrey,
                                        borderWidth: '1px',
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        '&:hover': {
                                            borderColor: textBlack,
                                            borderWidth: '1px',
                                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    Ver vagas
                                </Button>
                            </Stack>
                        </Box>

                        {/* ADDRESS BLOCK */}
                        <Box sx={{ pt: 2, borderTop: `1px solid ${structuralGrey}` }}>
                            <Typography variant="caption" sx={{ color: textGrey, display: 'block', mb: 1, fontFamily: 'JetBrains Mono' }}>
                                LOCALIZAÇÃO
                            </Typography>
                            <Typography variant="body2" sx={{ color: textBlack, maxWidth: '300px', lineHeight: 1.5, fontFamily: '"JetBrains Mono", monospace', fontWeight: 500 }}>
                                Universidade de Brasília (UnB)
                                <br />Parque Tecnológico
                                <br />Bloco H, 1o Andar
                                <br />Brasília - DF
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {/* RIGHT SECTOR: FORM (Clean White) */}
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{
                    width: { xs: '100%', md: '55%' },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'transparent',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {/* SCROLLABLE CONTENT */}
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 3, md: 6 },
                        gap: 3,
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#E0E0E0' }
                    }}>
                        {/* FIELDS */}
	                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
	                            <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono', color: textBlack, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
	                                Nome *
	                            </Typography>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Digite seu nome..."
                                required
                                autoComplete="name"
                                maxLength={120}
                                enterKeyHint="next"
                                aria-invalid={Boolean(errors.name)}
                                style={inputStyles}
                                onFocus={focusStyles}
                                onBlur={blurStyles}
                            />
	                        </Box>

	                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
	                            <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono', color: textBlack, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
	                                E-mail ou WhatsApp *
	                            </Typography>
                            <input
                                type="text"
                                name="contact"
                                value={formData.contact}
                                onChange={handleChange}
                                placeholder="seu.email@empresa.com ou (61) 90000-0000"
                                required
                                autoComplete="email"
                                inputMode={looksLikeEmailInput(formData.contact) ? 'email' : 'tel'}
                                maxLength={160}
                                enterKeyHint="next"
                                aria-invalid={Boolean(errors.contact)}
                                style={inputStyles}
                                onFocus={focusStyles}
                                onBlur={blurStyles}
                            />
                            {!!errors.contact && (
                                <Typography sx={{ fontSize: '0.78rem', color: theme.palette.primary.main, fontWeight: 600 }}>
                                    {errors.contact === 'invalid_email'
                                        ? 'Digite um e-mail válido.'
                                        : errors.contact === 'invalid_phone'
                                            ? 'Digite um WhatsApp válido com DDD.'
                                            : 'Preencha seu e-mail ou WhatsApp.'}
                                </Typography>
                            )}
	                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
	                            <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono', color: textBlack, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
	                                Como podemos ajudar? *
	                            </Typography>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Descreva seu projeto ou desafio..."
                                required
                                maxLength={2000}
                                enterKeyHint="send"
                                aria-invalid={Boolean(errors.message)}
                                style={{
                                    ...inputStyles,
                                    height: '180px',
                                    minHeight: '180px',
                                    resize: 'none'
                                }}
                                onFocus={focusStyles}
                                onBlur={blurStyles}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, alignItems: 'flex-start', mt: -0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <input
                                    type="checkbox"
                                    name="whatsappConsent"
                                    checked={Boolean(formData.whatsappConsent)}
                                    onChange={handleChange}
                                    style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: '#050505', cursor: 'pointer' }}
                                />
                                <Typography sx={{ color: textGrey, fontSize: '0.92rem', lineHeight: 1.5 }}>
                                    Quero receber a confirmação deste contato.
                                </Typography>
                            </Box>
                            <Button
                                type="submit"
                                disabled={status === 'submitting'}
                                variant="outlined"
                                endIcon={<ArrowForwardIcon />}
                                data-ui="contact-submit"
                                sx={{
                                    width: 'auto',
                                    maxWidth: '100%',
                                    borderRadius: '14px',
                                    borderColor: '#0A0A0A',
                                    borderWidth: '1px',
                                    bgcolor: '#0A0A0A',
                                    px: 4,
                                    py: 1.5,
                                    color: '#FFFFFF',
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '1rem',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 'none',
                                    transform: 'none !important',
                                    transition: 'background-color 0.22s ease, color 0.22s ease, border-color 0.22s ease',
                                    '&:hover': {
                                        transform: 'none !important',
                                        bgcolor: '#CC0000',
                                        color: '#FFFFFF',
                                        borderColor: '#CC0000',
                                        boxShadow: 'none',
                                        borderWidth: '1px'
                                    },
                                    '&:active': {
                                        transform: 'none !important',
                                        bgcolor: '#CC0000',
                                        color: '#FFFFFF',
                                        borderColor: '#CC0000',
                                        boxShadow: 'none'
                                    },
                                    '&:focus, &:focus-visible': {
                                        transform: 'none !important',
                                        boxShadow: 'none',
                                        outline: 'none',
                                    }
                                }}
                            >
                                {status === 'submitting' ? 'Enviando...' : 'Enviar Mensagem'}
                            </Button>
                        </Box>
                    </Box>

                </Box>
            </Box>
        </SectionWrapper>
    );
};

export default ContactPage;
